import { BaseWidget } from './BaseWidget';
import { SelectControl } from '../controls/SelectControl';
import { IconButtonGroupControl } from '../controls/IconButtonGroupControl';
import { ColorControl } from '../controls/ColorControl';
// (ColorControl is imported above for both the NestedItemControl child controls
// and the new widget-level graphColor)
import { NestedItemControl } from '../controls/NestedItemControl';
import { NumberCssControl } from '../controls/NumberCssControl';
import { PeriodOverrideControl } from '../controls/PeriodOverrideControl';
import useWidgetPeriod from './_shared/useWidgetPeriod';
import useWidgetPersona from './_shared/useWidgetPersona';
import usePrintDisplayMode from './_shared/usePrintDisplayMode';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { useFirestoreGeneral } from '../../../../firebase/useFirestore';
import { client } from '../../../../hooks/Client';
import { useCallback, useContext } from 'react';
import { fetchTemplateData } from '../utils/fetchTemplateData';
import { TemplateDataContext } from '../context/TemplateDataContext';
import { EditorContext } from '../context/EditorContext';
import EffectVisualization from '../../../dataVisualizations/EffectVisualization';
import WidgetSourcePlaceholder from '../components/WidgetSourcePlaceholder';
import LoadingSpinner from "../../../common/LoadingSpinner";
import notDeleted from '../../../../helpers/notDeleted';
import { definitionCollection } from '../../../../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// EffectWidget — picks one effect and renders its indicators via the shared
// `EffectVisualization`. Sidebar controls:
//   - `effectId`      Effect picker
//   - `displayMode`   navigator (default) / stacked
//   - `byNestedId`    collapsable accordion per indicator with graph-type +
//                     colour overrides (keyed by stable indicator ID)

/**
 * Resolve the indicators (QuestionnaireFields) attached to a given effect.
 * Used by NestedItemControl to enumerate per-indicator accordions.
 *
 * @param {string} effectId
 * @returns {Promise<Array<{id: string, label: string}>>}
 */
const resolveIndicators = async (effectId) => {
    if (!effectId) return [];
    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';

    if (isTemplate) {
        try {
            const blob = await fetchTemplateData();
            const all = blob?.data?.effects?.indicators || [];
            return all
                .filter(i => Array.isArray(i.EffectId) && i.EffectId.includes(effectId))
                .slice()
                .sort((a, b) => (a.IndicatorPosition ?? 0) - (b.IndicatorPosition ?? 0))
                .map(i => ({ id: i.ID, label: i.Question || i.Title || i.Type || 'Indicator' }));
        } catch (err) {
            console.error('EffectWidget: failed to fetch template indicators', err);
            return [];
        }
    }

    const snap = await definitionCollection('QuestionnaireFields')
        .where('CompagnyID', '==', client || '')
        .where('EffectId', 'array-contains', effectId)
        .orderBy('IndicatorPosition', 'asc')
        .get();
    return snap.docs.map(d => d.data()).filter(notDeleted).map(i => ({
        id: i.ID,
        label: i.Question || i.Title || i.Type || 'Indicator',
    }));
};

/**
 * @extends {BaseWidget}
 */
export class EffectWidget extends BaseWidget {
    getName() { return 'Effect'; }
    getCategory() { return 'research'; }
    getIcon() { return <AutoFixNormalIcon />; }

    getControls() {
        return {
            effectId: new SelectControl({
                label: 'Effect',
                description: 'Kies een effect',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const json = await fetchTemplateData();
                        values = json?.data?.effects?.effects || [];
                    } else {
                        const effects = await definitionCollection('OutputEffects')
                            .where('CompagnyID', '==', client || '')
                            .orderBy('Position', 'asc')
                            .get();
                        values = effects.docs.map(doc => doc.data()).filter(notDeleted);
                    }

                    return {
                        choices: values.map(o => ({ value: o.ID, label: o.Effect })),
                        allowSearch: true,
                    };
                },
            }),
            period: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de data van deze widget toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            displayMode: new IconButtonGroupControl({
                label: 'Weergave',
                description: 'Wissel tussen carousel (één indicator per keer) en gestapeld',
                defaultValue: 'navigator',
                options: {
                    buttons: [
                        { value: 'navigator', icon: <ViewCarouselIcon />, tooltip: 'Carousel' },
                        { value: 'stacked', icon: <ViewStreamIcon />, tooltip: 'Gestapeld' },
                    ],
                },
            }),
            graphColor: new ColorControl({
                label: 'Label- en legendakleur',
                description: 'Kleur van de labels en de legenda.',
                defaultValue: '',
            }),
            byNestedId: new NestedItemControl({
                label: 'Per indicator',
                description: 'Stel grafiektype en kleur in per indicator.',
                parentSettingKey: 'effectId',
                noParentText: 'Kies eerst een effect.',
                emptyText: 'Geen indicatoren gevonden voor dit effect.',
                nestedItemsResolver: (effectId) => resolveIndicators(effectId),
                children: {
                    graphType: new IconButtonGroupControl({
                        label: 'Grafiektype',
                        defaultValue: '',
                        options: {
                            buttons: [
                                { value: 'bar', icon: <BarChartIcon />, tooltip: 'Staafdiagram' },
                                { value: 'line', icon: <ShowChartIcon />, tooltip: 'Lijndiagram' },
                                { value: 'total-growth', icon: <TrendingUpIcon />, tooltip: 'Totale groei' },
                            ],
                        },
                    }),
                    color: new ColorControl({
                        label: 'Kleur',
                        defaultValue: '',
                    }),
                    /* Number-only styling for the 'total-growth' view, which
                       renders a single big "+X%" number. `visibleWhen` reads the
                       indicator's OWN graphType (sibling setting). */
                    numberFontSize: new NumberCssControl({
                        label: 'Tekstgrootte',
                        description: 'Grootte van het getal',
                        defaultValue: { value: 48, unit: 'px' },
                        options: { min: 8, max: 200, step: 1, units: ['px'] },
                        visibleWhen: (w) => w.getSetting('graphType') === 'total-growth',
                    }),
                    numberTextAlign: new IconButtonGroupControl({
                        label: 'Uitlijning',
                        description: 'Uitlijning van het getal',
                        defaultValue: 'center',
                        options: {
                            buttons: [
                                { value: 'left', icon: <FormatAlignLeftIcon />, tooltip: 'Links' },
                                { value: 'center', icon: <FormatAlignCenterIcon />, tooltip: 'Midden' },
                                { value: 'right', icon: <FormatAlignRightIcon />, tooltip: 'Rechts' },
                            ],
                        },
                        visibleWhen: (w) => w.getSetting('graphType') === 'total-growth',
                    }),
                    numberFontWeight: new SelectControl({
                        label: 'Dikte',
                        description: 'Dikte van het getal',
                        defaultValue: 'bold',
                        options: {
                            choices: [
                                { value: 'normal', label: 'Normaal' },
                                { value: 'bold', label: 'Vet' },
                                { value: 'bolder', label: 'Zwaarder' },
                                { value: 'lighter', label: 'Lichter' },
                            ],
                        },
                        visibleWhen: (w) => w.getSetting('graphType') === 'total-growth',
                    }),
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} widget={this} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.effectId
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {Object<string, Object<string, any>>} [props.byNestedId]
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Widget period setting (PeriodOverrideControl)
 * @param {EffectWidget} props.widget
 */
const Renderer = ({ effectId, displayMode = 'navigator', byNestedId, graphColor, period, widget }) => {
    const effectiveDisplayMode = usePrintDisplayMode(displayMode);
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);
    const { range: periodRange, startDate, endDate } = useWidgetPeriod(period);
    const editorCtx = useContext(EditorContext);
    const handleCurrentItemChange = useCallback((item) => {
        editorCtx?.setActiveNestedItemId?.(widget.id, item?.ID || null);
    }, [editorCtx, widget?.id]);

    const liveEffects = useFirestoreGeneral('OutputEffects', 'ID', isTemplate ? '' : (effectId || ''));
    const effect = isTemplate
        ? (templateData?.data?.effects?.effects || []).find(e => e.ID === effectId)
        : (Array.isArray(liveEffects) && liveEffects[0]);

    const personaId = useWidgetPersona();

    if (!effect) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* A selected effect that is still resolving ('' from the hook, or the
           template blob not loaded yet) renders nothing — don't flash the
           picker placeholder for one round-trip. An empty *array* means the
           ID doesn't exist in this tenant (orphan after a template copy):
           that does get the placeholder. */
        const stillResolving = isTemplate
            ? !templateData
            : (effectId && !Array.isArray(liveEffects));
        if (stillResolving) return <LoadingSpinner />;

        return <WidgetSourcePlaceholder ctaLabel="Selecteer effect" />;
    }

    return (
        <EffectVisualization
            effect={effect}
            displayMode={effectiveDisplayMode}
            nestedSettings={byNestedId || {}}
            graphColor={graphColor}
            selectedPersonaId={personaId}
            startDate={startDate}
            endDate={endDate}
            periodRange={periodRange}
            templateData={isTemplate ? templateData : null}
            onCurrentItemChange={handleCurrentItemChange}
        />
    );
};
